import React, { useState, useEffect, useCallback } from 'react';
import { Field, reduxForm } from 'redux-form';
import { InputField, InputFieldIcon } from '../inputs/standard';
import ErrorList from './ErrorList';
import Helper from '../helpers';
import zxcvbn from 'zxcvbn';
import { makeStyles } from '@material-ui/styles';
import IframePopup from './IframePopup';

const useStyles = makeStyles({
    strengthMeter: {
        position: 'relative',
        height: 3,
        background: '#ddd',
        margin: '7px 0',
        borderRadius: 2,
        '&:before': {
            content: '""',
            height: 'inherit',
            background: 'transparent',
            display: 'block',
            borderColor: '#fff',
            borderStyle: 'solid',
            borderWidth: '0 6px 0',
            position: 'absolute',
            width: 'calc(20% + 6px)',
            zIndex: 10,
            left: 'calc(20% - 3px)',
            boxSizing: 'border-box',
        },
        '&:after': {
            content: '""',
            height: 'inherit',
            background: 'transparent',
            display: 'block',
            borderColor: '#fff',
            borderStyle: 'solid',
            borderWidth: '0 6px 0',
            position: 'absolute',
            width: 'calc(20% + 6px)',
            zIndex: 10,
            right: 'calc(20% - 3px)',
            boxSizing: 'border-box',
        },
    },
    strengthMeterFill: {
        background: 'transparent',
        height: 'inherit',
        position: 'absolute',
        width: 0,
        borderRadius: 'inherit',
        boxSizing: 'border-box',
        transition: 'width 0.5s ease-in-out, background 0.25s',
        '&[data-strength="0"]': {
            width: '20%',
            background: 'grey',
        },
        '&[data-strength="1"]': {
            width: '40%',
            background: 'orangered',
        },
        '&[data-strength="2"]': {
            width: '60%',
            background: 'orange',
        },
        '&[data-strength="3"]': {
            width: '80%',
            background: 'yellowgreen',
        },
        '&[data-strength="4"]': {
            width: '100%',
            background: 'green',
        },
    },
    valid: {
        boxShadow: '0 0 1px 2px rgba(0, 128, 0, .5)',
        borderColor: 'green',
        borderRadius: 3,
        '&:focus': {
            outlineWidth: 3,
        },
    },
    strengthIndicator: {
        textAlign: 'right',
        fontWeight: 'bold',
        fontSize: 14,
        paddingRight: '8px',
        '&[data-strength="0"]': {
            color: 'grey',
        },
        '&[data-strength="1"]': {
            color: 'red',
        },
        '&[data-strength="2"]': {
            color: 'orangered',
        },
        '&[data-strength="3"]': {
            color: 'yellowgreen',
        },
        '&[data-strength="4"]': {
            color: 'green',
        },
    },
    inputField: {
        display: 'flex',
        position: 'relative',
    },
    inputFieldIcon: {
        position: 'absolute',
        right: '8px',
        top: '5px',
        cursor: 'pointer',
    },
});

let EmailForm = props => {
    const classes = useStyles();
    const [showPassword, setShowPassword] = useState(false);
    const { handleSubmit, strength, setStrength, isApps } = props;
    const [iframeLink, setIframeLink] = useState(null);

    const createIframePopup = useCallback(link => {
        return e => {
            setIframeLink(link);
        };
    }, []);

    useEffect(() => {
        if (props.formValues?.password) {
            setStrength(zxcvbn(props.formValues?.password).score);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let errors = Object.keys(props.errors).map((key, i) => {
        return window.VULOG_TRANSLATE(`${props.errors[key]}`);
    });

    if (props.error) {
        errors.push(window.VULOG_TRANSLATE(props.error));
    }

    let additionalCheckboxes = [];
    if (window.VULOG_CONFIG.EMAIL_REGISTRATION_CHECKBOXES && !props.login) {
        Object.keys(window.VULOG_CONFIG.EMAIL_REGISTRATION_CHECKBOXES).forEach(key => {
            additionalCheckboxes.push(
                <div className="form-row mb-2" key={key}>
                    <Field name={key} component="input" type="checkbox" className="custom-checkbox" id={key} />
                    {key === 'terms' ? (
                        <label htmlFor="terms">{Helper.generateTermsText(createIframePopup, isApps)}</label>
                    ) : key === 'privacy' ? (
                        <label htmlFor="privacy">{Helper.generatePrivacyText(createIframePopup, isApps)}</label>
                    ) : key === 'marketingConsent' ? (
                        <label htmlFor="marketingConsent">
                            {Helper.generateMarketingConsentText(createIframePopup)}
                        </label>
                    ) : key === 'profilingConsent' ? (
                        <label htmlFor="profilingConsent">
                            {Helper.generateProfilingConsentText(createIframePopup)}
                        </label>
                    ) : key === 'surveyConsent' ? (
                        <label htmlFor="surveyConsent">{Helper.generateSurveyConsentText(createIframePopup)}</label>
                    ) : key === 'shareDataConsent' ? (
                        <label htmlFor="shareDataConsent">
                            {Helper.generateShareDataConsentText(createIframePopup)}
                        </label>
                    ) : (
                        <label htmlFor={key}>{window.VULOG_TRANSLATE(`${key}_label`)}</label>
                    )}
                </div>
            );
        });
    }

    return (
        <form className="d-flex flex-column justify-content-center credentials-form" onSubmit={handleSubmit}>
            <div className="form-group">
                <label htmlFor="email" className="blue label-row">
                    <i className="material-icons blue va-middle medium-size cent">email</i>{' '}
                    {window.VULOG_TRANSLATE('Email')}
                </label>
                <Field
                    name="email"
                    disabled={props.disableEmailInputField}
                    placeholder={window.VULOG_TRANSLATE('Email')}
                    className="form-control"
                    component={InputField}
                    type="email"
                />
            </div>
            {window.VULOG_CONFIG.CONFIRM_EMAIL && (
                <div className="form-group">
                    <label htmlFor="email" className="blue label-row">
                        <i className="material-icons blue va-middle medium-size cent">email</i>{' '}
                        {window.VULOG_TRANSLATE('Retype Email')}
                    </label>
                    <Field
                        name="email2"
                        disabled={props.disableEmailInputField}
                        placeholder={window.VULOG_TRANSLATE('Retype Email')}
                        className="form-control"
                        component={InputField}
                        type="email"
                    />
                </div>
            )}
            <div className="form-group">
                <label htmlFor="password" className="blue label-row">
                    {' '}
                    <i className="material-icons blue va-middle medium-size cent">lock</i>{' '}
                    {window.VULOG_TRANSLATE('Password')}
                </label>
                <Field
                    name="password"
                    onChange={(e, value) => setStrength(zxcvbn(value).score)}
                    placeholder={window.VULOG_TRANSLATE('Password')}
                    className="form-control"
                    component={InputFieldIcon}
                    type="password"
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    inputField={classes.inputField}
                    inputFieldIcon={classes.inputFieldIcon}
                />
            </div>
            {window.VULOG_CONFIG.MIN_PASSWORD_STRENGTH &&
            window.VULOG_CONFIG.MIN_PASSWORD_STRENGTH > 0 &&
            !props.login &&
            props.formValues?.password ? (
                <>
                    <div className={classes.strengthMeter}>
                        <div className={classes.strengthMeterFill} data-strength={strength}></div>
                    </div>
                    <div className={classes.strengthIndicator} data-strength={strength}>
                        {strength === 0 && <div>{window.VULOG_TRANSLATE('Very weak')}</div>}
                        {strength === 1 && <div>{window.VULOG_TRANSLATE('Weak')}</div>}
                        {strength === 2 && <div>{window.VULOG_TRANSLATE('Fair')}</div>}
                        {strength === 3 && <div>{window.VULOG_TRANSLATE('Good')}</div>}
                        {strength === 4 && <div>{window.VULOG_TRANSLATE('Great')}</div>}
                    </div>
                    <div className="form-group">
                        <label htmlFor="password" className="blue label-row">
                            {' '}
                            <i className="material-icons blue va-middle medium-size cent">lock</i>{' '}
                            {window.VULOG_TRANSLATE('Retype Password')}
                        </label>
                        <Field
                            name="password2"
                            placeholder={window.VULOG_TRANSLATE('Retype Password')}
                            className="form-control"
                            component={InputField}
                            type={showPassword ? 'text' : 'password'}
                        />
                        {!window.VULOG_CONFIG.MIN_PASSWORD_STRENGTH ||
                            (window.VULOG_CONFIG.MIN_PASSWORD_STRENGTH === 0 && (
                                <span className={'password-rules'}>
                                    {window.VULOG_TRANSLATE('7+ characters w/min, 1 capital letter, 1 number')}
                                </span>
                            ))}
                    </div>
                </>
            ) : (
                !props.login && (
                    <div className="form-group">
                        <label htmlFor="password" className="blue label-row">
                            {' '}
                            <i className="material-icons blue va-middle medium-size cent">lock</i>{' '}
                            {window.VULOG_TRANSLATE('Retype Password')}
                        </label>
                        <Field
                            name="password2"
                            placeholder={window.VULOG_TRANSLATE('Retype Password')}
                            className="form-control"
                            component={InputField}
                            type={showPassword ? 'text' : 'password'}
                        />
                        {!window.VULOG_CONFIG.MIN_PASSWORD_STRENGTH &&
                            window.VULOG_CONFIG.MIN_PASSWORD_STRENGTH === 0 && (
                                <span className={'password-rules'}>
                                    {window.VULOG_TRANSLATE('7+ characters w/min, 1 capital letter, 1 number')}
                                </span>
                            )}
                    </div>
                )
            )}
            {!window.VULOG_CONFIG.ERROR_DISPLAY_TOP && <ErrorList errors={errors} />}
            {!props.login ? additionalCheckboxes : null}
            {props.login
                ? [
                      <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={props.isWrongFleetForBusiness || props.isTkExpired}
                      >
                          {window.VULOG_TRANSLATE('Sign in')}
                      </button>,
                      <div style={{ textAlign: 'center', marginTop: 10 }}>
                          <a href={window.VULOG_CONFIG.RESET_PASSWORD}>{window.VULOG_TRANSLATE('Forgot Password')}</a>
                      </div>,
                      <div style={{ textAlign: 'center', marginTop: 10 }}>
                          <div
                              className={'link noUnderline'}
                              onClick={e => {
                                  e.preventDefault();
                                  props.setLogin(false);
                              }}
                          >
                              {window.VULOG_TRANSLATE('Not a member yet?')}
                          </div>
                      </div>,
                  ]
                : [
                      <button
                          key={'sign-up'}
                          type="submit"
                          className="btn btn-primary"
                          disabled={props.isWrongFleetForBusiness || props.isTkExpired}
                      >
                          {window.VULOG_TRANSLATE('Sign up')}
                      </button>,
                      props.isBusiness ? (
                          <div key={'login'} style={{ textAlign: 'center', marginTop: 10 }}>
                              <div
                                  className={'link noUnderline'}
                                  onClick={e => {
                                      e.preventDefault();
                                      props.setLogin(true);
                                  }}
                              >
                                  {window.VULOG_TRANSLATE('Already have an account?')}
                              </div>
                          </div>
                      ) : null,
                  ]}
            <IframePopup link={iframeLink} onClose={() => setIframeLink(null)} isOpen={iframeLink} />
        </form>
    );
};

EmailForm = reduxForm({
    // a unique name for the form
    form: 'email',
})(EmailForm);

export default EmailForm;
